// @flow

import React from 'react';
import get from 'lodash.get';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Button } from 'dibs-elements/exports/Button';

import { commit } from '../mutations/DeleteProjectMutation';
import { postProjectToFA } from '../faPoster';

import styles from './styles/DeleteProject.scss';

import type { Environment } from 'react-relay';
import type { IntlShape } from 'react-intl';
import type { FormStatus } from '../types.flow';

type OnSuccessPayload = {|
    deletedProjectId: string,
    result: string,
|};

export type DeleteProjectProps = {|
    firmId: ?string,
    onSuccess?: (payload: OnSuccessPayload) => mixed,
    projectId: string,
    projectName: string,
    relayEnvironment: Environment,
    source: string,
    userId: string,
|};

type DeleteProjectComponentProps = {|
    ...DeleteProjectProps,
    intl: IntlShape,
    onClose: () => void,
|};

type DeleteProjectState = {|
    formStatus: FormStatus,
|};

export class DeleteProject extends React.Component<
    DeleteProjectComponentProps,
    DeleteProjectState
> {
    constructor(props: DeleteProjectComponentProps) {
        super(props);

        this.handleMutationError = this.handleMutationError.bind(this);
        this.handleMutationSuccess = this.handleMutationSuccess.bind(this);
        this.onConfirmDelete = this.onConfirmDelete.bind(this);
        this.onDismiss = this.onDismiss.bind(this);
        this.state = {
            formStatus: 'DEFAULT',
        };
    }

    handleMutationError: (error: mixed) => void;
    handleMutationSuccess: (payload: mixed) => void;
    onConfirmDelete: () => void;
    onDismiss: () => void;

    handleMutationError(error: mixed) {
        console.error('Delete project mutation failed', error);
        this.setState({ formStatus: 'ERROR' });
    }

    handleMutationSuccess(payload: OnSuccessPayload) {
        const { firmId, onSuccess, source, userId } = this.props;

        postProjectToFA({
            action: 'DELETE',
            firmId,
            project: {
                serviceId: get(payload, 'deleteProject.deletedProjectId'),
            },
            source,
            userId,
        });

        if (typeof onSuccess === 'function') {
            onSuccess(payload);
        }
    }

    onConfirmDelete() {
        const { projectId, relayEnvironment } = this.props;
        const { formStatus } = this.state;

        if (formStatus === 'LOADING') {
            return;
        }

        this.setState({ formStatus: 'LOADING' });

        commit(relayEnvironment, {
            projectId,
            onCompleted: this.handleMutationSuccess,
            onError: this.handleMutationError,
        });
    }

    onDismiss() {
        if (typeof this.props.onClose === 'function') {
            this.props.onClose();
        }
    }

    render() {
        const { projectName } = this.props;
        const { formStatus } = this.state;

        return (
            <div>
                <div className={styles.prompt}>
                    <div>
                        <FormattedMessage
                            id="dc.ProjectForms.DeleteProject.confirmation"
                            defaultMessage="Are you sure you want to delete"
                        />
                        <span className={styles.projectName}>{projectName}</span>
                        <span>?</span>
                    </div>
                    <div>
                        <span>
                            <FormattedMessage
                                id="dc.ProjectForms.DeleteProject.warning"
                                defaultMessage="It cannot be undone."
                            />
                        </span>
                    </div>
                </div>
                <div className={styles.ctaContainer}>
                    <Button
                        className={styles.button}
                        dataTn="dismiss-delete-project-button"
                        onClick={this.onDismiss}
                        type="secondary"
                    >
                        <FormattedMessage
                            id="dc.ProjectForms.DeleteProject.cancelCta"
                            defaultMessage="Cancel"
                        />
                    </Button>
                    <Button
                        className={styles.button}
                        dataTn="delete-project-button"
                        onClick={this.onConfirmDelete}
                    >
                        <FormattedMessage
                            id="dc.ProjectForms.DeleteProject.deleteCta"
                            defaultMessage="Yes, Delete"
                        />
                    </Button>
                </div>
                {formStatus === 'ERROR' && (
                    <div className={styles.errorMessage}>
                        <FormattedMessage
                            id="dc.ProjectForms.DeleteProject.errorMessage"
                            defaultMessage="An error occurred. Project deletion failed."
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default injectIntl(DeleteProject);
